import React, { Component } from 'react';
import { observer, inject } from 'mobx-react';

import analytics from 'js/services/Analytics';
import AppState from 'js/stores/AppState';
import { url } from 'js/services/Network';

import Button, { buttonHeight } from 'js/components/common/Button';
import Modal from 'js/components/core/Modal';
import AppContent from 'js/components/AppContent';
import Backdrop from 'js/components/core/Backdrop';
import Constraint from 'js/components/Constraint';
import ErrorDisplay from 'js/components/common/ErrorDisplay';
import CopyItem from 'js/components/core/CopyItem';

import PaymentMethodForm from '../PaymentMethodForm';
import ShippingInstructions from '../ShippingInstructions';
import TokenDisplay from './TokenDisplay';
import OverallStatus from './OverallStatus';
import EditToken from './EditToken';
import ReturnItemLink from './ReturnItemLink';
import HowItWorks from './HowItWorks';
import HowItWorksDisplay from './HowItWorksDisplay';
import DeliveryProgress from './DeliveryProgress';
import Notice from './Notice';
import LinkButton from './LinkButton';
import MarketplaceButton from './MarketplaceButton';
import ItemTitle from './ItemTitle';

@inject('page')
@observer
export default class TokenStatusPage extends Component {
  componentDidMount() {
    AppState.setNav(
      AppState.previousPage === 'UserDashboardPage'
        ? {
            mode: 'back',
            text: 'Dashboard',
            url: url('user-dashboard-page'),
          }
        : this.props.page.is_buyer
        ? {
            mode: 'back',
            text: 'My offers',
            url: url('my-tokens'),
          }
        : {
            mode: 'back',
            text: 'My items',
            url: url('my-items'),
          },
    );
  }

  render() {
    const page = this.props.page;
    return (
      <>
        <AppContent padded>
          <Backdrop>
            <Constraint>
              {page.has_order
                ? {
                    offer_requested_by_buyer: <OverallStatus>Offer pending</OverallStatus>,
                    no_response_from_seller: <OverallStatus>Offer expired</OverallStatus>,
                    accepted_by_seller: page.is_buyer ? (
                      <OverallStatus>Accepted, preparing to ship</OverallStatus>
                    ) : (
                      <OverallStatus>Ready to ship</OverallStatus>
                    ),
                    shipped_by_seller: <OverallStatus>Item shipped</OverallStatus>,
                    delivered_to_buyer: <OverallStatus>Item delivered</OverallStatus>,
                    not_accepted_by_seller: <OverallStatus>Offer declined</OverallStatus>,
                    not_accepted_by_buyer: <OverallStatus>Offer declined</OverallStatus>,
                    failed_to_secure_buyer_funds: page.is_buyer ? (
                      <OverallStatus>Update needed</OverallStatus>
                    ) : (
                      <OverallStatus>Buyer update needed</OverallStatus>
                    ),
                    failed_to_secure_buyer_funds_insufficient: page.is_buyer ? (
                      <OverallStatus>Update needed</OverallStatus>
                    ) : (
                      <OverallStatus>Buyer update needed</OverallStatus>
                    ),
                    buyer_updated_card_after_failure: <OverallStatus>Offer pending</OverallStatus>,
                    shipping_rate_changed: <OverallStatus>Offer failed</OverallStatus>,
                    could_not_be_shipped: <OverallStatus>Not shipped</OverallStatus>,
                    delivery_exception: <OverallStatus>Delivery exception</OverallStatus>,
                    not_shipped_by_seller: <OverallStatus>Not shipped</OverallStatus>,
                    return_not_shipped_by_buyer: <OverallStatus>Return not shipped</OverallStatus>,
                    order_completed: <OverallStatus>Transaction completed</OverallStatus>,
                    order_completed_with_error: <OverallStatus>Transaction error</OverallStatus>,
                    order_return_requested: <OverallStatus>Return initiated</OverallStatus>,
                    order_return_shipped: <OverallStatus>Return shipped</OverallStatus>,
                    order_return_delivered: <OverallStatus>Return delivered</OverallStatus>,
                    order_flagged_as_fraud: <OverallStatus>Offer failed</OverallStatus>,
                    order_return_completed: <OverallStatus>Return completed</OverallStatus>,
                  }[page.order_status] || <OverallStatus>Unknown status (E603)</OverallStatus>
                : {
                    awaiting_seller: <OverallStatus>Waiting for seller approval</OverallStatus>,
                    awaiting_buyer_confirm: page.is_seller ? (
                      <OverallStatus>Awaiting buyer confirmation</OverallStatus>
                    ) : (
                      <OverallStatus>Awaiting shipping instructions</OverallStatus>
                    ),
                    ready_to_meet: <OverallStatus>Ready to meet {page.is_seller ? 'buyer' : 'seller'}</OverallStatus>,
                    ready_to_ship: page.is_seller ? (
                      <OverallStatus>Ready to ship</OverallStatus>
                    ) : (
                      <OverallStatus>Awaiting shipping approval</OverallStatus>
                    ),
                  }[page.item_status] || <OverallStatus>Unknown status (E604)</OverallStatus>}
              <TokenDisplay />
            </Constraint>

            <ErrorDisplay>{page.error}</ErrorDisplay>
          </Backdrop>

          {page.has_order
            ? {
                accepted_by_seller: page.is_seller ? (
                  <Constraint>
                    <Notice>
                      <p>Awesome, it's sold!</p>
                      <p>Now let's ship it.</p>
                    </Notice>
                    <ShippingInstructions />
                  </Constraint>
                ) : (
                  <Constraint>
                    <Notice>Your item is getting prepared for shipment!</Notice>
                    <DeliveryProgress />
                  </Constraint>
                ),
                order_return_requested: page.is_buyer ? (
                  <Constraint>
                    <Notice>
                      <p>Your return has been initiated.</p>
                      <p>Let's ship the item back.</p>
                    </Notice>
                    <ShippingInstructions />
                  </Constraint>
                ) : (
                  <Constraint>
                    <Notice>
                      <p>The buyer is preparing to return the item.</p>
                      <p>It will ship soon.</p>
                    </Notice>
                    <DeliveryProgress />
                  </Constraint>
                ),
                shipped_by_seller: (
                  <>
                    <Constraint>
                      <DeliveryProgress />
                    </Constraint>
                  </>
                ),
                delivered_to_buyer: page.is_buyer ? (
                  <Constraint offset={`${buttonHeight / 2}rem`}>
                    <Button onClick={page.completeOrder} isLoading={page.isCompleting}>
                      Confirm and pay seller
                    </Button>

                    <Notice>
                      <p>Please look it over and verify it is as expected.</p>
                      <p>The transaction will automatically complete after 24 hours.</p>
                    </Notice>
                    <DeliveryProgress />
                    <ReturnItemLink onClick={page.returnOrder}>An issue with your order?</ReturnItemLink>
                  </Constraint>
                ) : (
                  <Constraint>
                    <Notice>
                      <p>The buyer has received the shipment.</p>
                      <p>Once it is verified your funds will be on the way!</p>
                    </Notice>
                    <DeliveryProgress />
                  </Constraint>
                ),
                failed_to_secure_buyer_funds: page.is_buyer ? (
                  <Constraint>
                    <Notice>
                      <p>
                        Your credit card failed to authorize when the seller accepted your offer. Please update your
                        payment method by clicking the button below:
                      </p>
                    </Notice>
                    <Button onClick={page.updateCreditCard}>Update payment method</Button>
                  </Constraint>
                ) : (
                  <Constraint>
                    <Notice>
                      <p>
                        The buyers credit card has failed to authorize. They were notified that their payment method
                        needs updating.
                      </p>
                      <p>We will let you know when they have done this.</p>
                    </Notice>
                  </Constraint>
                ),
                failed_to_secure_buyer_funds_insufficient: page.is_buyer ? (
                  <Constraint>
                    <Notice>
                      <p>
                        Your credit card failed to authorize when the seller accepted your offer. You have insufficient funds. Please update your
                        payment method by clicking the button below:
                      </p>
                    </Notice>
                    <Button onClick={page.updateCreditCard}>Update payment method</Button>
                  </Constraint>
                ) : (
                  <Constraint>
                    <Notice>
                      <p>
                        The buyers credit card has failed to authorize. The buyer has insufficient funds. They were notified that their payment method
                        needs updating.
                      </p>
                      <p>We will let you know when they have done this.</p>
                    </Notice>
                  </Constraint>
                ),
                buyer_updated_card_after_failure: page.is_seller ? (
                  <Constraint offset={`${buttonHeight / 2}rem`}>
                    <LinkButton to={url('token-label-page', page)}>Accept and print label</LinkButton>
                    <Notice>
                      <p>The buyer has updated their payment method.</p>
                      <p>Click the button to confirm and print the shipping label.</p>
                    </Notice>
                  </Constraint>
                ) : (
                  <Constraint>
                    <Notice>
                      <p>Thank you for updating your payment method.</p>
                      <p>
                        We are waiting on the seller to confirm the shipping selection you chose, check back here for
                        updates.
                      </p>
                    </Notice>
                  </Constraint>
                ),
                shipping_rate_changed: (
                  <Constraint>
                    <Notice>
                      This order has failed because the shipping rate changed before the order was accepted.
                    </Notice>
                  </Constraint>
                ),
                could_not_be_shipped: (
                  <Constraint>
                    <DeliveryProgress />
                  </Constraint>
                ),
                delivery_exception: (
                  <Constraint>
                    <DeliveryProgress />
                  </Constraint>
                ),
                not_shipped_by_seller: (
                  <Constraint>
                    <DeliveryProgress />
                  </Constraint>
                ),
                return_not_shipped_by_buyer: (
                  <Constraint>
                    <DeliveryProgress />
                  </Constraint>
                ),
                order_completed: page.is_buyer ? (
                  <Constraint>
                    <Notice>This purchase has been completed.</Notice>
                    <DeliveryProgress />
                  </Constraint>
                ) : (
                  <Constraint>
                    <Notice>
                      <p>This purchase has been completed.</p>
                      <p>Your money is on its way!</p>
                    </Notice>
                    <DeliveryProgress />
                  </Constraint>
                ),
                order_completed_with_error: (
                  <Constraint>
                    <Notice>This order has an error, please contact us (E9001).</Notice>
                  </Constraint>
                ),
                order_return_shipped: (
                  <Constraint>
                    <DeliveryProgress />
                  </Constraint>
                ),
                order_return_delivered: (
                  <Constraint>
                    <DeliveryProgress />
                  </Constraint>
                ),
                order_flagged_as_fraud: (
                  <Constraint>
                    <Notice>This order has failed, please contact us (E9002).</Notice>
                  </Constraint>
                ),
                order_return_completed: (
                  <Constraint>
                    <DeliveryProgress />
                  </Constraint>
                ),
              }[page.order_status] || (
                <Constraint>
                  <Notice>Unknown item status error, please contact us. (E9101)</Notice>
                </Constraint>
              )
            : {
                awaiting_seller: (
                  <Constraint offset="3rem" bottomOffset="1.5rem">
                    <CopyItem
                      from={{ opacity: 0, position: 'relative' }}
                      to={{ opacity: 1 }}
                      title="Give this to your seller:"
                      text={page.token_page_url}
                      onCopy={() => {
                        analytics.track('Offer Link Copied', {
                          'Item Price': page.price,
                          'Item Name': page.title,
                          'Payment Link': page.token_page_url,
                          'Event Initiated by': AppState.currentPage === 'CreatePaymentLinkPage' || AppState.currentPage === 'SellerLandingPage'
                            ? 'seller' : 'buyer'
                        });
                      }}
                    />

                    <Notice>
                      <div>
                        Or if you don’t have a seller, head to the marketplace to see some awesome{' '}
                        <ItemTitle>{page.title}</ItemTitle> deals:
                      </div>
                      <MarketplaceButton
                        onClick={() => {}}
                        target="_blank"
                        href={`https://www.facebook.com/marketplace/search/?query=${encodeURIComponent(page.title)}`}>
                        Find on Facebook Marketplace
                      </MarketplaceButton>
                    </Notice>

                    <HowItWorks onClick={page.showHowItWorks}>Learn more</HowItWorks>
                  </Constraint>
                ),
                awaiting_buyer_confirm: page.is_buyer ? (
                  <Constraint offset={`${buttonHeight / 2}rem`}>
                    <LinkButton to={url('token-confirm-page', page)}>Select shipping method</LinkButton>
                  </Constraint>
                ) : (
                  <Constraint>
                    <Notice>You will receive shipping instructions as soon as the buyer has confirmed shipping.</Notice>
                  </Constraint>
                ),
                ready_to_ship: page.is_seller ? (
                  <Constraint offset={`${buttonHeight / 2}rem`}>
                    <LinkButton to={url('token-label-page', page)}>Accept and print label</LinkButton>
                    <Notice>
                      <p>The buyer has chosen their desired shipping method.</p>
                      <p>Click the button to confirm and print the shipping label.</p>
                    </Notice>
                  </Constraint>
                ) : (
                  <Constraint>
                    <Notice>
                      We are waiting on the seller to confirm the shipping selection you chose, check back here for
                      updates.
                    </Notice>
                  </Constraint>
                ),
                ready_to_meet: page.is_seller ? (
                  <Constraint>
                    <Notice>
                      <p>We are waiting on the buyer to confirm the sale.</p>
                      <p>
                        When you meet up with your buyer and exchange the item, ask them to confirm the purchase by
                        clicking on the button that says 'Item received, pay the seller' on the status page.
                      </p>
                    </Notice>
                  </Constraint>
                ) : (
                  <Constraint offset={`${buttonHeight / 2}rem`}>
                    <Button onClick={page.completeOrder}>Item received, pay the seller</Button>
                    <Notice>
                      <p>Go and meet up with your seller to receive the item in person.</p>
                      <p>
                        Once you receive the item, click the button below to complete the transaction and pay your
                        seller.
                      </p>
                    </Notice>
                  </Constraint>
                ),
              }[page.item_status] || (
                <Constraint>
                  <Notice>Unknown item status error, please contact us. (E9100)</Notice>
                </Constraint>
              )}
        </AppContent>

        <Modal showModal={page.modalIsOpen} onCancel={page.closeModal} withCloseButton>
          <EditToken />
        </Modal>

        <Modal showModal={page.howItWorksIsOpen} onCancel={page.closeModal} withCloseButton>
          <HowItWorksDisplay />
        </Modal>

        <Modal showModal={page.showCreditCardModal} onCancel={page.closeModal} withCloseButton>
          <PaymentMethodForm />
        </Modal>
      </>
    );
  }
}
